import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import api, { BACKEND_ORIGIN } from "@/lib/api";
import { money } from "@/lib/format";
import { Brand } from "@/components/Brand";
import { sound } from "@/lib/sound";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function GuestPay() {
  const { code } = useParams();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const [guest, setGuest] = useState(null); // { token, share, name }

  useEffect(() => {
    api
      .get(`/guest/preview/${code}`)
      .then((r) => {
        setPreview(r.data);
        if (r.data.your_name) setName(r.data.your_name);
      })
      .catch((e) =>
        setError(e.response?.data?.detail || "This invite link is invalid or expired.")
      );
  }, [code]);

  const join = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Enter your name");
    if (!email.trim()) return toast.error("Enter an email for your receipt");
    setBusy(true);
    try {
      const r = await api.post("/guest/join", {
        invite_code: code,
        name: name.trim(),
        email: email.trim().toLowerCase(),
      });
      setGuest({
        token: r.data.access_token,
        share: r.data.participant?.share_amount,
        name: r.data.participant?.name,
      });
      toast.success("You're in — pay your share below");
      sound.play("success");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not join");
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    sound.play("tap");
    setBusy(true);
    try {
      const r = await api.post(
        "/guest/pay",
        { origin_url: BACKEND_ORIGIN, method: "card" },
        { __guestToken: guest.token }
      );
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6">
      <div className="w-full max-w-md hp-glass-strong rounded-[2rem] p-8 hp-fade-in">
        <div className="flex justify-center mb-7">
          <Brand size="lg" to={null} float />
        </div>

        {error ? (
          <p className="text-center text-muted-foreground">{error}</p>
        ) : !preview ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-[#c084fc]" />
          </div>
        ) : (
          <>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                {preview.creator_name} invited you to
              </p>
              <h1 className="text-2xl font-extrabold text-white mt-1">
                {preview.name}
              </h1>
              <p className="text-3xl font-black text-[#c084fc] mt-4">
                {money(guest?.share ?? preview.share_preview)}
              </p>
              <p className="text-xs text-muted-foreground">
                {preview.your_share_is_reserved ? "your share" : "your estimated share"}
              </p>
            </div>

            {!guest ? (
              <form onSubmit={join} className="mt-7 space-y-4">
                <div>
                  <Label className="text-white">Your name</Label>
                  <Input
                    data-testid="guest-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Alex"
                    className="mt-1.5 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Email (for your receipt)</Label>
                  <Input
                    data-testid="guest-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1.5 bg-white/5 border-white/10 text-white"
                  />
                </div>
                <Button
                  data-testid="guest-join-btn"
                  type="submit"
                  disabled={busy}
                  className="hp-cta w-full h-11 rounded-xl font-bold text-white border-0"
                >
                  {busy ? "Joining…" : "Continue"}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Want to pick your exact items instead of an even split?{" "}
                  <Link to="/start" className="text-[#c084fc] font-semibold">
                    Start your own
                  </Link>
                </p>
              </form>
            ) : (
              <div className="mt-7 space-y-3">
                <div className="flex items-center justify-center gap-2 text-[#34d399] text-sm">
                  <Check size={16} /> Joined as {guest.name}
                </div>
                <Button
                  data-testid="guest-pay-btn"
                  onClick={pay}
                  disabled={busy}
                  className="hp-cta w-full h-12 rounded-xl font-bold text-white text-base border-0"
                >
                  {busy ? "Redirecting…" : `Pay ${money(guest.share)}`}
                </Button>
                <p className="text-xs text-muted-foreground text-center">
                  Secure checkout powered by Stripe.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
